// Systems.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Typography, useTheme, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../state/reducers";
import AccordionPanel from "../components/system/AccordionPanel";
import ConfigDetails from "../components/system/ConfigDetails";
import SystemDetails from "../components/system/SystemDetails";
import SystemSettingsForm from "../components/system/forms/SystemSettingsForm";
import { bindActionCreators } from "@reduxjs/toolkit";
import {
  createSystem,
  deleteSystem
} from '../state/system/action_creator';

const Systems: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { selectedSystem, systems } = useSelector((state: State) => state.systemState);

  const [configs, setConfigs] = useState<any[]>([]);
  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<
    "clientProcess" | "assetProcessing" | "assetStorage" | "assetSyndication" | "systemInfo" | "createSystem"
  >("systemInfo");

  const [formData, setFormData] = useState<any>({});
  const [dirty, setDirty] = useState(false);


  const createSystemAction = bindActionCreators(createSystem, dispatch);
  const deleteSystemAction = bindActionCreators(deleteSystem, dispatch);

  // 🔹 When selectedSystem changes, update configs from systems array
  useEffect(() => {
    if (!selectedSystem) {
      setConfigs([]);
      return;
    }

    const system = Array.isArray(systems)
      ? systems.find((s: any) => s.systemCode === selectedSystem)
      : null;

    if (system && system.configurations) {
      const configsList = Object.entries(system.configurations).map(([name, value]) => ({
        name,
        ...(value as object),
      }));
      setConfigs(configsList);
    } else {
      setConfigs([]);
    }
  }, [selectedSystem, systems]);

  // 🔹 Auto-select default config
  useEffect(() => {
    if (selectedSection === "systemInfo" || selectedSection === "createSystem") return;

    if (!expandedConfigId && configs.length > 0) {
      const defaultCfg = configs.find((c: any) => c.clientProcess?.is_default);
      if (defaultCfg) {
        setExpandedConfigId(defaultCfg.name);
        setSelectedSection("clientProcess");
      }
    }
  }, [configs, expandedConfigId, selectedSection]);

  const handleChange = (key: string, value: any) => {
    console.log(key, value);
    setFormData((prev: any) => {
      // Create a deep clone so we don’t mutate state directly
      const updated = structuredClone(prev);

      // Support dotted paths like "configurations.TomsTest.clientProcess.vin_feedback_terminology"
      const keys = key.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in obj) || typeof obj[k] !== "object" || obj[k] === null) {
          obj[k] = {}; // ensure nested object exists
        }
        obj = obj[k];
      }

      // Set final value
      obj[keys[keys.length - 1]] = value;

      // Determine if something changed compared to baseSystem
      const isDifferent = JSON.stringify(updated) !== JSON.stringify(baseSystem);
      setDirty(isDifferent);

      return updated;
    });
  };

  const handleSave = () => {
    if (selectedSection === "createSystem") {
      console.log("🆕 Creating new system:", formData);
      createSystemAction(formData)
      
      setSelectedSection("systemInfo");
      setDirty(false);
    } else if (selectedSection === "systemInfo") {
      console.log("✅ Updating system:", formData);
      // TODO: call update-system Lambda
    } else {
      console.log("✅ Saving config:", selectedSection, formData);
      // TODO: call update-config Lambda
    }
    setDirty(false);
  };

  // get selected system & config
  const systemObj = useMemo(() => {
    if (!Array.isArray(systems)) return null; // safe guard
    return systems.find((s: any) => s.systemCode === selectedSystem) || null;
  }, [systems, selectedSystem]);

  const baseSystem = useMemo(() => {
    if (selectedSection === "createSystem") return formData;
    return systemObj || {};
  }, [systemObj, selectedSection]);

  const selectedConfig = configs.find((c: any) => c.name === expandedConfigId);
  const configSectionData = selectedConfig?.[selectedSection || ""];

  const canSave =
    (selectedSection === "createSystem" &&
      formData.systemCode?.trim() &&
      formData.commonName?.trim()) ||
    (selectedSection !== "createSystem" && dirty);

  const isLoadingSystem =
    !Array.isArray(systems) ||
    (selectedSystem && !systemObj);

  useEffect(() => {
    if (systemObj) {
      setFormData(systemObj);
      setDirty(false);
    }
  }, [systemObj]);

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden" }}>
      {/* Left accordion */}
      <AccordionPanel
        configs={configs}
        expandedConfigId={expandedConfigId}
        selectedSection={selectedSection}
        onAccordionChange={(id) => {
          setExpandedConfigId(id);
          setSelectedSection("clientProcess");
        }}
        onSectionSelect={(section) =>
          setSelectedSection(
            section as
            | "clientProcess"
            | "assetProcessing"
            | "assetStorage"
            | "assetSyndication"
            | "systemInfo"
          )
        }
        onSystemInfoSelect={() => {
          setExpandedConfigId(null);
          setSelectedSection("systemInfo");
          setConfigs(configs)
        }}
        onNewSystem={() => {
          // 🔹 Reset to blank state
          setSelectedSection("createSystem");
          setFormData({
            systemCode: "",
            unitIndex: "U01",
            commonName: "",
            cloudFrontBaseUrl: "",
            customerDealIdentifier: "",
            dailyAssetReportEmailList: "",
            IANA_timezone: "",
            configurations: {},
          });
          setConfigs([]); // ✅ clear configurations
          setExpandedConfigId(null); // collapse any open config
          setDirty(false);
        }}
      />


      {/* Right panel */}
      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        {isLoadingSystem ? (
          // 🔹 Show loading spinner centered
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : selectedSection === "createSystem" ? (
          <SystemSettingsForm system={formData} handleChange={handleChange} />
        ) : selectedSection === "systemInfo" ? (
          <SystemDetails system={{ ...systemObj, ...formData }} onChange={handleChange} />
        ) : configSectionData ? (
          <ConfigDetails
            system={{ ...systemObj, ...formData }}
            expandedConfigId={expandedConfigId}
            section={selectedSection}
            onChange={handleChange}
          />
        ) : (
          <Typography variant="body1" sx={{ mt: 4 }}>
            Select a section to view details
          </Typography>
        )}

        {/* Global Save/Create button */}
        {!isLoadingSystem && (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              {selectedSection === "createSystem"
                ? "Create System"
                : dirty
                  ? "Save Changes"
                  : "No Changes"}
            </Button>

            {selectedSection !== "createSystem" && systemObj && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete system "${systemObj.systemCode}"? This action cannot be undone.`
                    )
                  ) {
                    deleteSystemAction(systemObj.systemCode);
                  }
                }}
              >
                Delete System
              </Button>
            )}
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default Systems;
